import React, { useState, useEffect } from 'react';
import { API_URL } from './apiConfig';
import { Ban, Flag, MessageCircle } from 'lucide-react';

// Shown once on a match that Maia (the companion) opened for both people (25 Oct 2026). Honest provenance, nothing more.
const ViaMaia = ({ small }) => (
  <span title="Maia introduced you both, and you each said yes" style={{ display: 'inline-block', fontSize: small ? '10px' : '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: 'rgba(124,106,247,0.12)', color: '#7c6af7', marginLeft: '6px', verticalAlign: 'middle' }}>
    via Maia
  </span>
);

export default function MatchesView({
  matches, archivedMatches, showArchivedMatches, setShowArchivedMatches,
  selectedMatch, setSelectedMatch,
  messages, messageInput, setMessageInput,
  user, onSendMessage,
  onSelectMatch, onArchiveMatch, onUnarchiveMatch,
  onBlockUser, onOpenReportModal,
  setShowDateModal, onNeedsVerification,
}) {
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'ai' | 'archived'
  const [aiMatches, setAiMatches] = useState([]);
  const [aiMatchesLoading, setAiMatchesLoading] = useState(false);
  const [respondingId, setRespondingId] = useState(null);

  const loadAiMatches = async () => {
    setAiMatchesLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/companion/matching/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.success) setAiMatches(d.data || []);
    } catch (e) {}
    finally { setAiMatchesLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'ai') loadAiMatches();
  }, [activeTab]);

  const respondToAiMatch = async (matchId, response) => {
    setRespondingId(matchId);
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_URL}/companion/matching/respond`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchId, response }) // 'accept' | 'decline'
      });
      setAiMatches(prev => prev.filter(m => m._id !== matchId));
    } catch (e) {}
    finally { setRespondingId(null); }
  };

  // Sync tab state with showArchivedMatches prop
  const effectiveTab = showArchivedMatches ? 'archived' : activeTab === 'archived' ? 'active' : activeTab;

  return (
          <div className="fade-in" style={{ padding: '20px' }}>
            {!selectedMatch && (
              <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setActiveTab('active'); setShowArchivedMatches(false); }}
                  style={{
                    padding: '10px 20px', borderRadius: '12px', border: 'none',
                    background: effectiveTab === 'active' ? 'var(--primary)' : 'var(--surface)',
                    color: effectiveTab === 'active' ? 'white' : 'var(--text-primary)',
                    fontWeight: 600, cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s'
                  }}
                >
                  💬 Active
                </button>
                <button
                  onClick={() => { setActiveTab('ai'); setShowArchivedMatches(false); }}
                  style={{
                    padding: '10px 20px', borderRadius: '12px', border: 'none',
                    background: effectiveTab === 'ai' ? 'var(--primary)' : 'var(--surface)',
                    color: effectiveTab === 'ai' ? 'white' : 'var(--text-primary)',
                    fontWeight: 600, cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  ✨ AI Suggested
                  {aiMatches.length > 0 && effectiveTab !== 'ai' && (
                    <span style={{
                      position: 'absolute', top: '-4px', right: '-4px',
                      background: '#ef4444', color: '#fff', borderRadius: '50%',
                      width: '18px', height: '18px', fontSize: '10px', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>{aiMatches.length}</span>
                  )}
                </button>
                <button
                  onClick={() => { setActiveTab('archived'); setShowArchivedMatches(true); }}
                  style={{
                    padding: '10px 20px', borderRadius: '12px', border: 'none',
                    background: effectiveTab === 'archived' ? 'var(--primary)' : 'var(--surface)',
                    color: effectiveTab === 'archived' ? 'white' : 'var(--text-primary)',
                    fontWeight: 600, cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s'
                  }}
                >
                  📦 Archived
                </button>
              </div>
            )}
            {/* AI Suggested tab content */}
            {!selectedMatch && effectiveTab === 'ai' && (
              <div>
                {aiMatchesLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                    Maia is thinking...
                  </div>
                ) : aiMatches.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>✨</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>
                      No suggestions yet
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: '280px', margin: '0 auto' }}>
                      Maia reviews profiles in the background. When she finds someone she thinks you'd genuinely connect with, they'll appear here.
                    </div>
                    <div style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      Make sure AI matching is enabled in Settings → AI Matching
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {aiMatches.map(match => (
                      <div key={match._id} style={{
                        background: 'var(--surface)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid rgba(124,106,247,0.2)'
                      }}>
                        {/* Maia's reasoning — shown first, profile revealed after */}
                        <div style={{ padding: '16px', borderBottom: '1px solid var(--border, rgba(255,255,255,0.08))' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <span style={{ fontSize: '20px' }}>✨</span>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: '#a78bfa' }}>Maia thinks you'd connect</span>
                          </div>
                          {match.reasoning && (
                            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, fontStyle: 'italic' }}>
                              "{match.reasoning}"
                            </p>
                          )}
                          {match.compatibilityScore && (
                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{ flex: 1, height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{ height: '4px', width: `${match.compatibilityScore}%`, background: '#7c6af7', borderRadius: '2px' }} />
                              </div>
                              <span style={{ fontSize: '12px', color: '#a78bfa', fontWeight: 600, flexShrink: 0 }}>
                                {match.compatibilityScore}% match
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Profile reveal — graduated disclosure */}
                        <div style={{ padding: '16px' }}>
                          {match.profileRevealed ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              {match.photo && (
                                <img src={match.photo} alt="" style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover' }} />
                              )}
                              <div>
                                <div style={{ fontWeight: 700, fontSize: '16px', color: 'var(--text-primary)' }}>{match.firstName}</div>
                                <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{match.age}{match.location ? ` · ${match.location}` : ''}</div>
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>
                              Would you like to see who this is?
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '10px', marginTop: match.profileRevealed ? '14px' : 0 }}>
                            <button
                              disabled={respondingId === match._id}
                              onClick={() => respondToAiMatch(match._id, 'accept')}
                              style={{
                                flex: 1, padding: '12px', borderRadius: '12px', border: 'none',
                                background: '#7c6af7', color: '#fff', fontWeight: 600,
                                fontSize: '14px', cursor: respondingId === match._id ? 'not-allowed' : 'pointer',
                                opacity: respondingId === match._id ? 0.6 : 1
                              }}
                            >
                              {match.profileRevealed ? "I'm interested 💜" : "Show me 💜"}
                            </button>
                            <button
                              disabled={respondingId === match._id}
                              onClick={() => respondToAiMatch(match._id, 'decline')}
                              style={{
                                flex: 1, padding: '12px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'none',
                                color: 'var(--text-secondary)', fontWeight: 600,
                                fontSize: '14px', cursor: respondingId === match._id ? 'not-allowed' : 'pointer'
                              }}
                            >
                              Pass
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {selectedMatch ? (
              <div>
                <button
                  onClick={() => setSelectedMatch(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginBottom: '16px',
                    fontWeight: 600
                  }}
                >
                  ← Back to matches
                </button>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px',
                  padding: '16px',
                  background: '#f8f9fa',
                  borderRadius: '12px'
                }}>
                  <img
                    src={selectedMatch.user.photos?.[0]?.url || 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80'}
                    alt={selectedMatch.user.firstName}
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                      {selectedMatch.user.firstName}{selectedMatch.viaMaia && <ViaMaia />}
                    </h4>
                    <p style={{ margin: 0, fontSize: '12px', color: '#7f8c8d' }}>
                      {selectedMatch.user.distance || 'Nearby'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDateModal(true)}
                    style={{
                      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 4px 12px rgba(255,107,107,0.3)',
                      transition: 'all 0.3s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(255,107,107,0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(255,107,107,0.3)';
                    }}
                  >
                    📅 Date
                  </button>
                  <button
                    onClick={() => onArchiveMatch(selectedMatch.matchId)}
                    title="Archive chat"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      border: 'none',
                      background: '#e3f2fd',
                      color: '#2196f3',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      transition: 'all 0.2s'
                    }}
                  >
                    📦
                  </button>
                  <button
                    onClick={() => onBlockUser(selectedMatch.user.userId, 'matches')}
                    title="Block user"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      border: 'none',
                      background: '#fee2e2',
                      color: '#e74c3c',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Ban size={18} />
                  </button>
                  <button
                    onClick={() => onOpenReportModal(selectedMatch.user.userId, selectedMatch.user.firstName, 'matches')}
                    title="Report user"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      border: 'none',
                      background: '#fef3c7',
                      color: '#e67e22',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Flag size={18} />
                  </button>
                </div>

                <div style={{
                  background: 'var(--surface-dim)',
                  borderRadius: '12px',
                  padding: '20px',
                  minHeight: '300px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginBottom: '16px'
                }}>
                  {messages[selectedMatch.matchId]?.length > 0 ? (
                    messages[selectedMatch.matchId].map((msg, i) => (
                      <div key={i} style={{
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: msg.sender === user.id ? 'flex-end' : 'flex-start'
                      }}>
                        <div style={{
                          background: msg.sender === user.id ? 'var(--msg-mine)' : 'var(--msg-theirs)',
                          color: msg.sender === user.id ? 'var(--msg-mine-text)' : 'var(--msg-theirs-text)',
                          padding: '10px 14px',
                          borderRadius: '18px',
                          maxWidth: '70%',
                          fontSize: '14px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                          {msg.content}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      color: '#95a5a6',
                      padding: '60px 20px'
                    }}>
                      <p>Say hi to {selectedMatch.user.firstName}! 👋</p>
                    </div>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px'
                }}>
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        onSendMessage();
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      borderRadius: '24px',
                      border: '2px solid #e9ecef',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={onSendMessage}
                    style={{
                      background: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '48px',
                      height: '48px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ➤
                  </button>
                </div>
              </div>
            ) : effectiveTab !== 'ai' ? (
              <>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: 700,
                  marginBottom: '8px',
                  color: '#2c3e50'
                }}>
                  Your Matches
                </h3>
                <p style={{
                  color: '#7f8c8d',
                  marginBottom: '24px',
                  fontSize: '14px'
                }}>
                  💬 Start chatting for free!
                </p>

                {(() => {
                  const displayMatches = showArchivedMatches ? archivedMatches : matches;
                  
                  return displayMatches.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 20px',
                      color: '#95a5a6'
                    }}>
                      <MessageCircle size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                      <p>{showArchivedMatches ? 'No archived matches' : 'No matches yet. Keep swiping!'}</p>
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      {displayMatches.map((match, idx) => (
                      <div
                        key={idx}
                        onClick={() => user?.isIdentityVerified ? onSelectMatch(match) : onNeedsVerification()}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '16px',
                          padding: '16px',
                          background: 'white',
                          borderRadius: '16px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                          position: 'relative',
                          opacity: user?.isIdentityVerified ? 1 : 0.7
                        }}
                      >
                        {!user?.isIdentityVerified && (
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(102, 126, 234, 0.9)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            🔒 Verify to Chat
                          </div>
                        )}
                        <img
                          src={match.user.photos?.[0]?.url || 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80'}
                          alt={match.user.firstName}
                          style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            filter: !user?.isIdentityVerified ? 'blur(4px)' : 'none'
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <h4 style={{
                            margin: '0 0 4px 0',
                            fontSize: '17px',
                            fontWeight: 600
                          }}>
                            {match.user.firstName}, {match.user.age}{match.viaMaia && <ViaMaia small />}
                          </h4>
                          <p style={{
                            margin: 0,
                            fontSize: '12px',
                            color: '#7f8c8d'
                          }}>
                            {user?.isIdentityVerified ? (match.viaMaia ? 'Matched through Maia — say hello' : 'Start a conversation') : 'Verify to unlock'}
                          </p>
                        </div>
                        {showArchivedMatches && user?.isIdentityVerified ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onUnarchiveMatch(match.matchId);
                            }}
                            style={{
                              padding: '8px 16px',
                              borderRadius: '12px',
                              border: 'none',
                              background: '#e3f2fd',
                              color: '#2196f3',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Unarchive
                          </button>
                        ) : (
                          <div style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: '#51cf66'
                          }} />
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); onBlockUser(match.user.userId, 'matches'); }}
                          title="Block user"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: 'none',
                            background: '#fee2e2',
                            color: '#e74c3c',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Ban size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); onOpenReportModal(match.user.userId, match.user.firstName, 'matches'); }}
                          title="Report user"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: 'none',
                            background: '#fef3c7',
                            color: '#e67e22',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Flag size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </>
            ) : null}
          </div>
  );
}
